export interface Producto {
  productoId: number;
  negocioId: number;
  nombreNegocio: string;
  nombre: string;
  descripcion: string | null;
  precio: number;
  imagenUrl: string | null;
  esDelDia: boolean;
  estaActivo: boolean;
  fechaCreacion: string;
}

export interface CreateProductoDto {
  negocioId: number;
  nombre: string;
  descripcion: string | null;
  precio: number;
  imagenUrl: string | null;
  esDelDia: boolean;
  estaActivo: boolean;
}

export interface UpdateProductoDto {
  negocioId: number;
  nombre: string;
  descripcion: string | null;
  precio: number;
  imagenUrl: string | null;
  esDelDia: boolean;
  estaActivo: boolean;
}

class ProductosService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://localhost:7123/api';
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Error en la conexión con el servidor',
      }));
      throw new Error(errorData.message || `Error HTTP: ${response.status}`);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  async getAll(): Promise<Producto[]> {
    return this.request<Producto[]>('/productos');
  }

  async getByNegocio(negocioId: number): Promise<Producto[]> {
    return this.request<Producto[]>(`/productos/negocio/${negocioId}`);
  }

  async getById(id: number): Promise<Producto> {
    return this.request<Producto>(`/productos/${id}`);
  }

  async create(data: CreateProductoDto): Promise<Producto> {
    return this.request<Producto>('/productos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async update(id: number, data: UpdateProductoDto): Promise<Producto> {
    return this.request<Producto>(`/productos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(id: number): Promise<void> {
    await this.request<void>(`/productos/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadImage(id: number, file: File): Promise<{ imagenUrl: string }> {
    const url = `${this.baseUrl}/productos/${id}/upload-imagen`;
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Error al subir la imagen',
      }));
      throw new Error(errorData.message || `Error HTTP: ${response.status}`);
    }

    return response.json();
  }
}

export const productosService = new ProductosService();
